import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface OsmMapRoadMeta {
  copyright?: string;
  attribution?: string;
  license?: string;
  generator?: string | null;
  bounds?: {
    minlat: number;
    minlon: number;
    maxlat: number;
    maxlon: number;
  } | null;
  element_count?: number;
  road_count?: number;
}

export interface OsmMapRoadResponse {
  success: boolean;
  data: GeoJSON.FeatureCollection & { meta?: OsmMapRoadMeta };
  error?: string;
  code?: string;
}

export function friendlyOsmOverlayStatus(err: any): string {
  const body = err?.error || {};
  const code = String(body.code || err?.code || '');
  const raw = String(body.error || err?.message || '');
  const lower = raw.toLowerCase();

  if (
    code === 'EXTENT_TOO_DENSE' ||
    lower.includes('maxcontentlength') ||
    lower.includes('too many nodes') ||
    lower.includes('zoom in')
  ) {
    return 'Zoom in to load OSM roads';
  }
  if (code === 'OSM_TIMEOUT' || lower.includes('timeout') || lower.includes('econnaborted')) {
    return 'OSM request timed out';
  }
  if (
    code === 'OSM_BUSY' ||
    err?.status === 429 ||
    err?.status === 504 ||
    err?.status === 509 ||
    lower.includes('busy')
  ) {
    return 'OSM is busy — try again';
  }
  if (code === 'OSM_FETCH_FAILED' || err?.status === 502 || lower.includes('failed to load osm')) {
    return 'OSM roads unavailable — zoom or retry';
  }
  return 'OSM roads unavailable — zoom or retry';
}

/** Fetches highway ways from OpenStreetMap Map API via backend proxy. */
@Injectable({ providedIn: 'root' })
export class OsmMapApiService {
  private readonly apiUrl = `${environment.BASE_URL}lrs/osm/map`;

  constructor(private http: HttpClient) {}

  /**
   * @param bbox WGS84 — west, south, east, north (max ~0.25 deg² per OSM policy)
   */
  fetchRoadNetwork(bbox: {
    west: number;
    south: number;
    east: number;
    north: number;
    zoom?: number;
  }): Observable<OsmMapRoadResponse> {
    let params = new HttpParams()
      .set('west', String(bbox.west))
      .set('south', String(bbox.south))
      .set('east', String(bbox.east))
      .set('north', String(bbox.north));
    if (bbox.zoom != null && Number.isFinite(bbox.zoom)) {
      params = params.set('zoom', String(Math.round(bbox.zoom * 10) / 10));
    }
    return this.http.get<OsmMapRoadResponse>(this.apiUrl, { params });
  }
}
